import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box, Grid} from "@mui/material";
import {useNavigate} from "react-router";
import axios from "axios";
import DataTable from "../../../../component/DataTable/DataTable";
import {addCustomRenderer} from "../../../../component/DataTable/tableUtils";
import {AppSnackbar, ConfirmDialog, LabelledMultiSelect, SearchBar, SelectAccountDialog, StyledButtonContained} from "../../../../utils/uiUtils";
import {ORDER_SHIPPING_STATUSES, ORDER_PROCESSING_STATUSES} from "../../../../utils/constants";
import {deleteJob, fetchCustomers, fetchJobs} from "../../../../utils/apiCalls";
import {fetchCompanyList} from "../../../../utils/dashboardCalls";
import {ORDER_LIST_TABLE_COLUMNS} from "./jobListTableConfig";
import {getParentStageLabel, getStateLabel, mapShippingModeText, renderOrderCell, renderOrderActions, renderOrderStatus} from "./jobListRenderHelpers";

const stageOptions = [
    {key: "draft", value: "Draft", statuses: ["draft"]},
    {key: "shipment", value: "Shipment", statuses: ORDER_SHIPPING_STATUSES},
    {key: "processing", value: "Processing", statuses: ORDER_PROCESSING_STATUSES},
];

export const AdminJobList = () => {
    const navigate = useNavigate();

    const [selectedClient, setSelectedClient] = useState([]);
    const [customerList, setCustomerList] = useState([]);
    const [selectedStages, setSelectedStages] = useState(stageOptions.map(s => s.key));
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [orderCount, setOrderCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [addOrderDialog, setAddOrderDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [deleteTargetRow, setDeleteTargetRow] = useState(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [customerArkbridgeMap, setCustomerArkbridgeMap] = useState({});
    const [companyCodeMap, setCompanyCodeMap] = useState({});
    const [clientsLoaded, setClientsLoaded] = useState(false);
    // Incremented on every loadJobData call — background description fetches check this to
    // discard results that arrived after the user navigated to a different page.
    const enrichmentEpoch = useRef(0);

    const handleManageOrder = useCallback((row) => {
        navigate(`/JobManagement/${row.uuid}`, {state: {data: row}});
    }, [navigate]);

    const handleEditOrder = useCallback((row) => {
        navigate(`/JobCreation?uuid=${row.uuid}`);
    }, [navigate]);

    const handleDeleteOrder = useCallback((row) => {
        setDeleteTargetRow(row);
        setConfirmDeleteOpen(true);
    }, []);

    const handleConfirmDelete = () => {
        deleteJob(deleteTargetRow.uuid)
            .then(() => {
                setConfirmDeleteOpen(false);
                setDeleteTargetRow(null);
                loadJobData();
            })
            .catch(error => {
                console.error('Failed to delete job:', error);
                setErrorMessage(`Failed to delete job ${deleteTargetRow?.order_number}.`);
                setConfirmDeleteOpen(false);
                setDeleteTargetRow(null);
            });
    };

    const columnsConfig = useMemo(() => addCustomRenderer(ORDER_LIST_TABLE_COLUMNS, {
        "Job": (row) => renderOrderCell(row, searchQuery),
        "Status": renderOrderStatus,
        "Actions": (row) => renderOrderActions(row, {
            handleManage: handleManageOrder,
            handleEdit: handleEditOrder,
            handleDelete: handleDeleteOrder,
        }),
    }), [searchQuery, handleManageOrder, handleEditOrder, handleDeleteOrder]);

    const handlePageChange = (event, page) => setCurrentPage(page);

    const [rawJobList, setRawJobList] = useState([]);
    const [jobDescriptions, setJobDescriptions] = useState({});

    const loadClientList = useCallback((signal) => {
        /*
         * The signal goes in the options object, third. It was being passed first, where `search` is:
         * `{signal}` stringified into the query as `search=[object Object]`, so this asked the server for
         * customers whose name contains that literal text and the client filter came back empty. It also
         * made `cache: !search` false, so the list was re-fetched on every visit to this page.
         *
         * The shape reads like fetchJobs below, which takes its options second — this one takes search
         * and page first, and the mistake was copying that call.
         */
        fetchCustomers("", null, {signal})
            .then(customers => {
                setCustomerList(customers);
                setSelectedClient(customers.map(e => e.company_name));
                const arkMap = {};
                customers.forEach(c => { arkMap[c.uuid] = parseInt(c.arkbridge_customer_id, 10); });
                setCustomerArkbridgeMap(arkMap);
                setClientsLoaded(true);
            })
            .catch(error => {
                if (axios.isCancel(error)) {
                    console.log('Client list fetch cancelled on unmount.');
                    return;
                }
                console.error('Failed to load client list:', error);
                setErrorMessage('There was an error loading the client list.');
                setClientsLoaded(true); // Ensure job loading can proceed even if clients fail
            });
    }, []); // This function has a stable identity

    const loadJobData = useCallback((signal) => {
        enrichmentEpoch.current += 1;
        setJobDescriptions({});
        const allStagesSelected = selectedStages.length === stageOptions.length;
        const expandedStatuses = allStagesSelected ? [] : stageOptions
            .filter(s => selectedStages.includes(s.key))
            .flatMap(s => s.statuses);
        fetchJobs({
            orderTypes: [],
            orderStatuses: expandedStatuses,
            search: searchQuery,
            page: currentPage,
            customerNames: selectedClient,
        }, {signal})
            .then(data => {
                setRawJobList(data.results);
                setOrderCount(data.count);
                setTotalPages(Math.ceil(data.count / 10));
            })
            .catch(error => {
                if (axios.isCancel(error)) {
                    console.log('Job list fetch cancelled on unmount.');
                    return;
                }
                console.error('Failed to load jobs:', error);
                setErrorMessage('There was an error loading the job list.');
            });
    }, [currentPage, selectedClient, selectedStages, searchQuery]);

    // Initial data loading sequence
    useEffect(() => {
        const controller = new AbortController();
        loadClientList(controller.signal);
        return () => controller.abort();
    }, [loadClientList]);

    useEffect(() => {
        if (!clientsLoaded) return;

        const controller = new AbortController();
        loadJobData(controller.signal);

        return () => controller.abort();
    }, [clientsLoaded, loadJobData]);

    // Derived: enrich raw jobs with display labels + tapeark identifier + descriptions.
    // Recomputes instantly when maps or descriptions arrive — no re-fetch needed.
    const jobList = useMemo(() => {
        return rawJobList.map(row => {
            const arkId = customerArkbridgeMap[row.customer];
            const code = companyCodeMap[arkId];
            const tapearkId = row.tapeark_job_id > 0 && code
                ? `${code}-${String(row.tapeark_job_id).padStart(3, '0')}`
                : null;
            return {
                ...row,
                item_id: row.uuid,
                shipping_mode_label: mapShippingModeText(row.shipping_mode),
                status_label: (() => {
                    const label = getStateLabel(row.status);
                    const parent = getParentStageLabel(row.status);
                    return parent && parent !== label ? `${label} (${parent})` : label;
                })(),
                tapeark_identifier: tapearkId,
                job_description: jobDescriptions[row.tapeark_job_id] ?? null,
            };
        });
    }, [rawJobList, customerArkbridgeMap, companyCodeMap, jobDescriptions]);

    useEffect(() => {
        let isMounted = true;

        // fetchCompanyList() posts to the dashboard proxy and handles caching itself
        // (see dashboardCalls.postDashboard), so no local URL or sessionStorage handling here.
        fetchCompanyList()
            .then(data => {
                if (!isMounted) return;
                const companies = data?.result?.data;
                if (!Array.isArray(companies)) return;
                const codeMap = {};
                companies.forEach(company => {
                    codeMap[company.company_id] = company.code;
                });
                setCompanyCodeMap(codeMap);
            })
            .catch(err => {
                if (!isMounted || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                console.error('Failed to fetch company list:', err);
                setErrorMessage('Could not load company data. Tape Ark identifiers may not display correctly.');
            });

        return () => {
            isMounted = false;
        };
    }, []);

    return (
        <Grid container spacing={0}>
            {/* Filter bar */}
            <Grid item xs={12}>
                <Grid container alignItems="center" spacing={2} sx={{mt: 2, py: 2}}>

                    {/* Search */}
                    <Grid item xs={3} sx={{display: 'flex', justifyContent: 'flex-start'}}>
                        <SearchBar
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onClear={() => setSearchQuery('')}
                        />
                    </Grid>

                    {/* Status */}
                    <Grid item>
                        <LabelledMultiSelect
                            label="Status"
                            options={stageOptions}
                            selected={selectedStages}
                            onChange={setSelectedStages}
                        />
                    </Grid>

                    {/* Client */}
                    <Grid item>
                        <LabelledMultiSelect
                            label="Client"
                            options={customerList.map(c => ({key: c.company_name, value: c.company_name}))}
                            selected={selectedClient}
                            onChange={setSelectedClient}
                            minWidth={150}
                        />
                    </Grid>

                    {/* Spacer */}
                    <Grid item xs/>

                    {/* Add Order */}
                    <Grid item xs={2} sx={{display: 'flex', justifyContent: 'flex-end'}}>
                        <StyledButtonContained
                            type="button"
                            size="large"
                            fullWidth={true}
                            variant="contained"
                            onClick={() => setAddOrderDialog(true)}
                            style={{width: "inherit"}}
                        >
                            Add Job
                        </StyledButtonContained>
                    </Grid>
                </Grid>
            </Grid>

            {/* Table */}
            <Grid item xs={12}>
                <DataTable
                    data={jobList}
                    columnsConfig={columnsConfig}
                    dataContainsFormatting={false}
                    searchQuery={searchQuery}
                    showPagination={true}
                    entriesCount={orderCount}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    handlePageChangeCallback={handlePageChange}
                />
            </Grid>

            {/* Add Order dialog */}
            <SelectAccountDialog
                open={addOrderDialog}
                onClose={() => setAddOrderDialog(false)}
                customers={customerList}
                onConfirm={(uuid) => navigate('/JobCreation', {state: {uuid}})}
            />

            {/* Delete confirmation */}
            <ConfirmDialog
                open={confirmDeleteOpen}
                onClose={() => { setConfirmDeleteOpen(false); setDeleteTargetRow(null); }}
                onConfirm={handleConfirmDelete}
                title="Delete Job"
                message={`Are you sure you want to delete job ${deleteTargetRow?.order_number}? This action cannot be undone.`}
            />

            <AppSnackbar
                open={!!errorMessage}
                message={errorMessage}
                severity="error"
                onClose={() => setErrorMessage('')}
            />
        </Grid>
    );
};
